import * as React from 'react';

import { Button } from '@/components/ui/button';
import type { CguVersionStateDto } from '@/api/legal-admin-client';
import {
  useCguVersions,
  useCguVersionsCount,
  useDownloadCguVersionPdf,
} from '@/api/use-legal-admin';
import { stateLabel } from '@/routes/admin/cgu/cgu-format';
import { CguComparison } from '@/routes/admin/cgu/cgu-comparison';
import { CguSignaturesContent } from '@/routes/admin/cgu/cgu-signatures-route';
import { CguDraftCreate } from '@/routes/admin/cgu/cgu-draft-create';
import { CguTabs, type CguTabDef } from '@/routes/admin/cgu/cgu-tabs';
import { CguVersionDetail } from '@/routes/admin/cgu/cgu-version-detail';
import {
  CguVersionsTable,
  type CguSortColumn,
  type CguSortDirection,
} from '@/routes/admin/cgu/cgu-versions-table';
import { triggerPdfDownload } from '@/routes/admin/cgu/cgu-pdf-download';

// =============================================================================
// cgu-registre-screen.tsx — Registre CGU à 4 onglets (A-CGU ÉTAPE 2 + R15-GOV-05). Présentation contrôlée.
// =============================================================================
// Onglet actif + version sélectionnée vivent dans l'URL (RULE_REF_006) — pilotés par le parent
// (adaptateur routeur). Le tri du registre est un état de VUE mineur, en mémoire (useState).
//   (a) Versions    : registre GET versions (7 colonnes gelées + nbSignatures), tri, compteur ;
//   (b) Détail/Éditeur : GET versions/:id (contenu + métadonnées) + éditeur (Brouillon) OU
//       création d'un brouillon (quand aucune version sélectionnée) ;
//   (c) Comparaison : vue côte-à-côte brouillon ⟷ version publiée (R15-GOV-05, composition front) ;
//   (d) Journal     : réutilise le journal des signatures existant (IP masquée à la source).
// Onglets accessibles (WAI-ARIA Tabs, clavier flèches/Home/Fin) via CguTabs.

/** Identifiants stables des onglets (sérialisés en URL). */
export type CguRegistreTab = 'versions' | 'detail' | 'comparaison' | 'journal';

const TABS: readonly CguTabDef[] = [
  { id: 'versions', label: 'Versions' },
  { id: 'detail', label: 'Détail / Éditeur' },
  { id: 'comparaison', label: 'Comparaison' },
  { id: 'journal', label: 'Journal des signatures' },
];

/** Cible de navigation ATOMIQUE : onglet ET version en UN seul changement d'URL. */
export interface CguRegistreNavTarget {
  readonly tab: CguRegistreTab;
  readonly version: string | undefined;
}

export interface CguRegistreScreenProps {
  readonly activeTab: CguRegistreTab;
  readonly selectedVersionId: string | undefined;
  // Navigation ATOMIQUE (onglet + version d'un coup). Deux setters séparés (onSelectTab puis
  // onSelectVersion) se clobbaient dans l'adaptateur : chaque navigate() reconstruisait TOUTE la
  // search depuis une closure périmée, le 2e écrasant le 1er → `version` perdu à l'« Ouvrir » (la
  // vue Détail retombait sur la création). Un seul appel garantit tab+version cohérents.
  readonly onNavigate: (target: CguRegistreNavTarget) => void;
}

export function CguRegistreScreen({
  activeTab,
  selectedVersionId,
  onNavigate,
}: CguRegistreScreenProps): React.JSX.Element {
  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Registre des CGU</h1>
        <p className="text-muted-foreground">
          Versions des Conditions Générales d'Utilisation : cycle de vie, édition,
          publication et suivi des signatures.
        </p>
      </header>

      <CguTabs
        tabs={TABS}
        activeId={activeTab}
        onSelect={(id) => onNavigate({ tab: id as CguRegistreTab, version: selectedVersionId })}
        ariaLabel="Sections du registre des CGU"
      >
        {activeTab === 'versions' ? (
          <VersionsTab
            onOpen={(id) => onNavigate({ tab: 'detail', version: id })}
            onCreate={() => onNavigate({ tab: 'detail', version: undefined })}
          />
        ) : null}

        {activeTab === 'detail' ? (
          selectedVersionId !== undefined ? (
            <CguVersionDetail
              cguVersionId={selectedVersionId}
              onDeleted={() => onNavigate({ tab: 'versions', version: undefined })}
            />
          ) : (
            <CguDraftCreate
              onCreated={(id) => onNavigate({ tab: 'versions', version: id })}
            />
          )
        ) : null}

        {activeTab === 'comparaison' ? <CguComparison /> : null}

        {activeTab === 'journal' ? <CguSignaturesContent /> : null}
      </CguTabs>
    </div>
  );
}

// --- Onglet (a) — Registre des versions ---

function VersionsTab({
  onOpen,
  onCreate,
}: {
  readonly onOpen: (id: string) => void;
  readonly onCreate: () => void;
}): React.JSX.Element {
  const versions = useCguVersions();
  const count = useCguVersionsCount();
  const downloadPdf = useDownloadCguVersionPdf();

  const [sortColumn, setSortColumn] = React.useState<CguSortColumn>('createdAt');
  const [sortDirection, setSortDirection] = React.useState<CguSortDirection>('desc');

  const [statusFilter, setStatusFilter] = React.useState<CguVersionStateDto | 'all'>('all');
  const [periodFrom, setPeriodFrom] = React.useState('');
  const [periodTo, setPeriodTo] = React.useState('');

  const filtered = React.useMemo(() => {
    const all = versions.data ?? [];
    return all.filter((v) => {
      if (statusFilter !== 'all' && v.state !== statusFilter) return false;
      if (periodFrom !== '' && v.createdAt < new Date(periodFrom).toISOString()) return false;
      if (periodTo !== '' && v.createdAt > new Date(periodTo + 'T23:59:59.999Z').toISOString())
        return false;
      return true;
    });
  }, [versions.data, statusFilter, periodFrom, periodTo]);

  const onSort = React.useCallback((column: CguSortColumn): void => {
    setSortColumn((prevColumn) => {
      if (prevColumn === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return prevColumn;
      }
      setSortDirection('asc');
      return column;
    });
  }, []);

  const onDownloadPdf = React.useCallback(
    (id: string): void => {
      const row = versions.data?.find((v) => v.cguVersionId === id);
      downloadPdf.mutate(id, {
        onSuccess: (bytes) => {
          triggerPdfDownload(bytes, `${row?.versionNumber ?? id}.pdf`);
        },
      });
    },
    [downloadPdf, versions.data],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" role="status">
          {count.isSuccess
            ? `${String(count.data.count)} version${count.data.count > 1 ? 's' : ''} au registre`
            : 'Comptage des versions…'}
        </p>
        <Button type="button" onClick={onCreate}>
          Nouveau brouillon
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4" role="group" aria-label="Filtres du registre">
        <div className="space-y-1">
          <label htmlFor="cgu-filter-status" className="text-xs font-medium text-muted-foreground">Statut</label>
          <select id="cgu-filter-status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CguVersionStateDto | 'all')}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="all">Tous</option>
            <option value="Brouillon">{stateLabel('Brouillon')}</option>
            <option value="Publiee">{stateLabel('Publiee')}</option>
            <option value="Archivee">{stateLabel('Archivee')}</option>
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="cgu-filter-from" className="text-xs font-medium text-muted-foreground">Créée depuis</label>
          <input id="cgu-filter-from" type="date" value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm" />
        </div>
        <div className="space-y-1">
          <label htmlFor="cgu-filter-to" className="text-xs font-medium text-muted-foreground">Créée jusqu'au</label>
          <input id="cgu-filter-to" type="date" value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm" />
        </div>
      </div>

      {downloadPdf.isError ? (
        <p role="alert" className="text-destructive">
          Téléchargement du PDF impossible : {downloadPdf.error.message}
        </p>
      ) : null}

      {versions.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement du registre…
        </p>
      ) : versions.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement : {versions.error.message}
        </p>
      ) : versions.data.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune version de CGU pour le moment.
        </p>
      ) : filtered.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune version ne correspond aux filtres.
        </p>
      ) : (
        <CguVersionsTable
          rows={filtered}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          onOpen={onOpen}
          onDownloadPdf={onDownloadPdf}
          downloadingId={downloadPdf.isPending ? downloadPdf.variables ?? null : null}
        />
      )}
    </div>
  );
}
